import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sun, Moon, LogOut } from "lucide-react";
import { useCmdAuth } from "../contexts/CmdAuthContext";
import { useCmdTheme } from "../contexts/CmdThemeContext";
import { usePermissions } from "../hooks/usePermissions";
import { cmdApi } from "../api";

const SHORTCUTS = {
  p: "/cmd/portfolio", b: "/cmd/board", d: "/cmd/decisions",
  f: "/cmd/financials", r: "/cmd/reports", v: "/cmd/review",
  i: "/cmd/insights", c: "/cmd/capital",
};

const TABS = [
  { to: "/cmd/portfolio",  label: "Portfolio",  module: "portfolio" },
  { to: "/cmd/board",      label: "Board",      module: "board" },
  { to: "/cmd/decisions",  label: "Decisions",  module: "decisions" },
  { to: "/cmd/financials", label: "Financials", module: "financials" },
  { to: "/cmd/reports",    label: "Reports",    module: "reports" },
  { to: "/cmd/insights",   label: "Insights",   module: "financials" },
  { to: "/cmd/capital",    label: "Capital",    module: "capital" },
  { to: "/cmd/review",     label: "Review",     module: "review" },
  { to: "/cmd/access",     label: "Access",     module: "access", founderOnly: true },
  { to: "/cmd/settings",   label: "Settings",   module: "settings", founderOnly: true },
];

function initials(name = "") {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function todayLongEnIN() {
  // Example: "Friday, 6 February 2026"
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

export default function CmdShell({ children }) {
  const { me, logout } = useCmdAuth();
  const { theme, toggle } = useCmdTheme();
  const { visible, isFounder } = usePermissions();
  const navigate = useNavigate();
  const [decisionBadge, setDecisionBadge] = useState(0);

  useEffect(() => {
    if (!visible("decisions")) return;
    let alive = true;
    const fetchBadge = () =>
      cmdApi.get("/decisions/badge").then(({ data }) => { if (alive) setDecisionBadge(data.count); }).catch(() => {});
    fetchBadge();
    const t = setInterval(fetchBadge, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prompt 9: "g then <key>" quick-nav keyboard shortcuts
  useEffect(() => {
    let waiting = false;
    const inField = (el) => {
      const t = (el?.tagName || "").toLowerCase();
      return t === "input" || t === "textarea" || t === "select" || el?.isContentEditable;
    };
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inField(e.target)) return;
      const k = e.key.toLowerCase();
      if (!waiting && k === "g") { waiting = true; setTimeout(() => { waiting = false; }, 1200); return; }
      if (waiting && SHORTCUTS[k]) { waiting = false; e.preventDefault(); navigate(SHORTCUTS[k]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (!me) return null;
  const u = me.user;

  const handleLogout = async () => { await logout(); navigate("/cmd/login"); };

  const previewRaw = typeof window !== "undefined" && sessionStorage.getItem("cmd_preview");
  const preview = previewRaw ? JSON.parse(previewRaw) : null;
  const exitPreview = () => {
    sessionStorage.removeItem("cmd_preview");
    delete cmdApi.defaults.headers["X-Preview-As"];
    delete cmdApi.defaults.headers["X-Preview-Verticals"];
    window.location.href = "/cmd/access";
  };

  const visibleTabs = TABS.filter((t) => (t.founderOnly ? isFounder : visible(t.module)));

  return (
    <div className="cmd-root">
      {preview && (
        <div className="cmd-preview-banner" data-testid="preview-banner">
          Previewing as {preview.role}{preview.verticals?.length ? ` · ${preview.verticals.join(", ")}` : ""}
          <button className="cmd-btn cmd-btn-ghost" style={{ marginLeft: 12, padding: "4px 12px", fontSize: 13 }}
            onClick={exitPreview} data-testid="exit-preview">Exit preview</button>
        </div>
      )}
      <header className="cmd-header" data-testid="cmd-header">
        <div className="cmd-header-inner cmd-container">
          <div className="cmd-brand">
            EEPL Command Room<span className="dot">.</span>
          </div>
          <div className="cmd-today" data-testid="cmd-today">{todayLongEnIN()}</div>
          <div className="cmd-user">
            <button
              className="cmd-icon-btn"
              onClick={toggle}
              aria-label="Toggle theme"
              data-testid="cmd-theme-toggle"
              title={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="cmd-avatar" aria-hidden="true">{initials(u.name)}</div>
            <div className="cmd-user-meta">
              <div data-testid="cmd-user-name">{u.name}</div>
              <div className="role" data-testid="cmd-user-role">{me.role}</div>
            </div>
            <button
              className="cmd-icon-btn"
              onClick={handleLogout}
              aria-label="Sign out"
              data-testid="cmd-logout"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <nav className="cmd-tabs" data-testid="cmd-tabs">
        <div className="cmd-container" style={{ display: "flex", gap: 4 }}>
          {visibleTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) => `cmd-tab ${isActive ? "active" : ""}`}
              data-testid={`cmd-tab-${t.module}`}
            >
              {t.label}
              {t.module === "decisions" && decisionBadge > 0 && (
                <span className="cmd-tab-badge" data-testid="decisions-badge">{decisionBadge}</span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="cmd-container" style={{ padding: "28px 20px 80px" }}>
        {children}
      </main>
    </div>
  );
}
